import React, { useState, useEffect, useCallback } from 'react';
import './assets/style.less';
import { emitter } from '../../event';

type InventoryItem = {
    id: number;
    slot: number;
    name: string;
    count: number;
    icon: string;
    desc?: string;
    equipped?: boolean;
    editable?: boolean;
    onSale?: boolean;
};

type InventoryData = {
    show: boolean;
    maxSlots: number;      // общий лимит слотов (как раньше)
    occupied?: number;     // сколько занято всего (не только на текущей странице)
    currentSkin: number;
    page?: number;         // текущая страница (0..totalPages-1)
    pageSize?: number;     // сколько слотов рисуем на странице
    totalPages?: number;   // всего страниц
    items: InventoryItem[];
};

export const Inventory = () => {
    const [visible, setVisible] = useState(false);
    const [data, setData] = useState<InventoryData>({
        show: false,
        maxSlots: 30,
        currentSkin: 0,
        items: []
    });
   // const [invDbg, setInvDbg] = useState<{ chars: number; bytes: number } | null>(null);// DEBUG INFO
    const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

    // FIX: чтобы пустые слоты не считались "selected" (undefined === undefined)
    const isItemSelected = (item?: InventoryItem | null) =>
        !!selectedItem && !!item && selectedItem.id === item.id;

    const closeInventory = useCallback(() => {
        setVisible(false);
        setSelectedItem(null);
        window.cef.set_focus(false);
        window.cef.emit('cef:inventoryClose');
    }, []);

    const escFunction = useCallback(
        (event: KeyboardEvent) => {
            if (event.keyCode === 27 && visible) {
                closeInventory();
            }
        },
        [visible, closeInventory]
    );

    const handleAction = (action: 'use' | 'drop' | 'info' | 'store'| 'sell' | 'edit') => {
    if (!selectedItem) return;

    // нельзя выбрасывать 2998/2999
    if (action === 'drop' && (selectedItem.id === 2998 || selectedItem.id === 2999)) return;

    if (action === 'store') {
        if (selectedItem.id < 2000) return;
        if (selectedItem.id === 2998 || selectedItem.id === 2999) return;
        window.cef.emit('cef:inventoryAction', 'store', selectedItem.id, 0);
        return;
    }

    if (action === 'use' && selectedItem.equipped && selectedItem.id < 2000) {
        window.cef.emit('cef:inventoryAction', 'unequip', selectedItem.id, 0);
        return;
    }

    if (action === 'edit') {
        if (!selectedItem.equipped) return;
        window.cef.emit('cef:inventoryAction', 'edit', selectedItem.id, 0);
        return;
    }

    window.cef.emit('cef:inventoryAction', action, selectedItem.id, 0);
};

    const isSellableStackItem = (item: InventoryItem) => {
    return item.id < 2000 && (item.icon === 'drugs' || item.icon === 'ammo' || item.icon === 'metal');
    };

const isSellableAccessory = (item: InventoryItem) => {
    // 1000..1999 = аксессуары (не надетые лежат в рюкзаке)
    return item.id >= 1000 && item.id < 2000;
};

const isSellableStoredSkin = (item: InventoryItem) => {
    // 2000..2997 = сохранённые скины
    return item.id >= 2000 && item.id < 2998;
};


    const getUseButtonText = () => {
        if (!selectedItem) return 'Использовать';
        if (selectedItem.id >= 2000) return 'Переодеться';
        if (selectedItem.id >= 1000 && selectedItem.id < 2000) {
            return selectedItem.equipped ? 'Снять' : 'Надеть';
        }
        return 'Использовать';
    };

    useEffect(() => {
        const unsubscribe = emitter.subscribe('cef:inventory', (json: any) => {
            try {
//debug info
 //               const s = typeof json === 'string' ? json : JSON.stringify(json);
/*setInvDbg({
  chars: s.length,
  bytes: new TextEncoder().encode(s).length
}); */
//debug info end
                const parsed = JSON.parse(json) as Partial<InventoryData>;
                const result: InventoryData = {
                    show: typeof parsed.show === 'boolean' ? parsed.show : false,
                    maxSlots: typeof parsed.maxSlots === 'number' ? parsed.maxSlots : 30,
                    currentSkin: typeof parsed.currentSkin === 'number' ? parsed.currentSkin : 0,
                    occupied: typeof parsed.occupied === 'number' ? parsed.occupied : undefined,
                    page: typeof parsed.page === 'number' ? parsed.page : undefined,
                    pageSize: typeof parsed.pageSize === 'number' ? parsed.pageSize : undefined,
                    totalPages: typeof parsed.totalPages === 'number' ? parsed.totalPages : undefined,
                    items: Array.isArray(parsed.items) ? parsed.items : []
                };

                setData(result);
                setVisible(result.show);
                window.cef.set_focus(result.show);
                setSelectedItem((prev) => {
                    if (!prev) return null;
                    const updated = result.items.find((it) => it.id === prev.id);
                    return updated || null;
                });
            } catch (e) {
                console.error('Inventory JSON Error', e);
            }
        });

        document.addEventListener('keydown', escFunction);
        return () => {
            unsubscribe();
            document.removeEventListener('keydown', escFunction);
        };
    }, [escFunction]);

    const getItemImage = (iconName: string) => {
        if (!iconName) return null;
        try {
            if (iconName.startsWith('skin_')) {
                const skinId = iconName.replace('skin_', '');
                return require(`./assets/skins/${skinId}.png`);
            }
            return require(`./assets/icons/${iconName}.webp`);
        } catch (err) {
            return null;
        }
    };

    if (!visible) return null;

    // --- SKINS: фикс логики 5 слотов ---
    const currentSkinItem = data.items.find((i) => i.id === 2999) || null; // "надето"
    const saveSkinItem = data.items.find((i) => i.id === 2998) || null; // фракционный/сохраненный

    // обычные скины (3 слота), исключаем 2998/2999
    const storedSkins = data.items
        .filter((i) => i.id >= 2000 && i.id !== 2998 && i.id !== 2999)
        .sort((a, b) => a.id - b.id);

    const equippedItems = data.items.filter((item) => item.equipped === true && item.id < 2000);
    const backpackItems = data.items.filter((item) => !item.equipped && item.id < 2000);

    const page = data.page ?? 0;
    const totalPages = data.totalPages ?? 1;
    const pageSize = data.pageSize ?? data.maxSlots; // если сервер не прислал - рендерим как раньше

    const occupiedTotal = data.occupied ?? backpackItems.length;

    const goToPage = (p: number) => {
        if (p < 0 || p >= totalPages) return;
        // Сервер сам пришлёт обновлённый JSON через cef:inventory
        window.cef.emit('cef:inventoryAction', 'page', p, 0);
    };

    const renderEquippedGrid = () => {
        const slots = [];
        for (let i = 0; i < 6; i++) {
            const item = equippedItems[i];
            const iconSrc = item ? getItemImage(item.icon) : null;
            const isSelected = isItemSelected(item);

            slots.push(
                <div
                    key={`eq-${i}`}
                    className={`equip-box ${isSelected ? 'selected' : ''}`}
                    onClick={() => item && setSelectedItem(item)}
                    title={item ? item.name : 'Пустой слот'}
                >
                    {item ? (
                        iconSrc ? (
                            <img src={iconSrc} alt="" />
                        ) : (
                            <div className="no-icon">{item.name[0]}</div>
                        )
                    ) : (
                        <div className="empty-plus">+</div>
                    )}
                </div>
            );
        }
        return <div className="equipped-grid-small">{slots}</div>;
    };

    const renderSkinSlots = () => {
        const maxSkinSlots = 5;

        // слоты: 0 = текущий (2999), 1..3 = сохраненные, 4 = фракционный (2998)
        const slotsData: Array<InventoryItem | null> = [currentSkinItem];

        for (let i = 0; i < 3; i++) {
            slotsData.push(storedSkins[i] || null);
        }

        slotsData.push(saveSkinItem);

        const slots = [];
        for (let i = 0; i < maxSkinSlots; i++) {
            const item = slotsData[i] || null;
            const isSelected = isItemSelected(item);
            const iconSrc = item ? getItemImage(item.icon) : null;

            const isFactionSlot = i === 4;
            const isFactionSkin = item?.id === 2998;
            const isCurrentSkin = item?.id === 2999;

            slots.push(
                <div
                    key={`skin-${i}`}
                    className={`skin-slot ${isSelected ? 'selected' : ''} ${isCurrentSkin ? 'equipped-skin' : ''} ${
                        isFactionSlot ? 'skin-slot-faction' : ''
                    }`}
                    onClick={() => item && setSelectedItem(item)}
                    title={
                        isFactionSlot
                            ? isFactionSkin
                                ? 'Фракционный скин (можно только надеть)'
                                : 'Слот фракционного скина'
                            : item
                            ? item.name
                            : 'Пустой слот'
                    }
                >
                    <div className="skin-circle">
                        {item && iconSrc ? (
                            <img src={iconSrc} alt={item.name} className="skin-img" />
                        ) : (
                            <div className="skin-placeholder">{isFactionSlot ? '★' : '👕'}</div>
                        )}
                    </div>

                    {item && (
                        <div className="skin-label">
                            {isCurrentSkin ? 'Надето' : isFactionSkin ? 'Специальный слот (Для организаций)' : item.name}
                        </div>
                    )}
                </div>
            );
        }

        return slots;
    };

    const renderBackpackSlots = () => {
        const slots = [];
        for (let i = 0; i < pageSize; i++) {
            const item = backpackItems[i];
            const isSelected = isItemSelected(item);
            const iconSrc = item ? getItemImage(item.icon) : null;

            slots.push(
                <div
                    key={i}
                    className={`inv-slot ${isSelected ? 'selected' : ''}`}
                    onClick={() => (item ? setSelectedItem(item) : setSelectedItem(null))}
                >
                    {item && (
                        <>
                            {iconSrc ? (
                                <img src={iconSrc} alt="" className="item-img" />
                            ) : (
                                <div className="item-icon-placeholder">{item.name[0]}</div>
                            )}
                            <div className="item-name">{item.name}</div>
                            {item.count > 1 && <div className="item-count">{item.count}</div>}
                        </>
                    )}
                </div>
            );
        }
        return slots;
    };

    const renderInfoPanel = () => {
        const currentSkinImg = getItemImage(`skin_${data.currentSkin}`);

        // 1) Ничего не выбрано
        if (!selectedItem) {
            return (
                <div className="info-panel-content">
                    <div className="info-header">
                        <div className="info-title">Персонаж</div>
                        <div className="info-big-icon">
                            {currentSkinImg ? (
                                <img src={currentSkinImg} alt="skin" />
                            ) : (
                                <div style={{ fontSize: '3vw' }}>🧍</div>
                            )}
                        </div>
                    </div>

                    <div className="info-scrollable-content">
                        <div className="desc-label">Аксессуары:</div>
                        {renderEquippedGrid()}
                    </div>

                    <div className="desc-label">Выберите предмет</div>
                </div>
            );
        }

        // 2) Выбран СКИН
        if (selectedItem.id >= 2000) {
            const isCurrentSkin = selectedItem.id === 2999;
            const isFactionSkin = selectedItem.id === 2998;
            const skinImg = getItemImage(selectedItem.icon);

            return (
                <div className="info-panel-content">
                    <div className="info-header">
                        <div className="info-title">{selectedItem.name}</div>
                        <div className="info-big-icon">
                            {skinImg ? <img src={skinImg} alt="" /> : <div style={{ fontSize: '3vw' }}>👕</div>}
                        </div>
                    </div>

                    <div className="info-scrollable-content">
                        <div className="desc-label">Аксессуары:</div>
                        {renderEquippedGrid()}
                    </div>

                    <div className="info-actions">
                        {isCurrentSkin ? (
                            <div className="status-badge">Надето на вас</div>
                        ) : (
                            <>
                                <button onClick={() => handleAction('use')} className="btn-use">
                                    Надеть
                                </button>

                                {!isFactionSkin && (
                                    <button onClick={() => handleAction('drop')} className="btn-drop">
                                        Выбросить
                                    </button>
                                )}

                                {!isFactionSkin && !isCurrentSkin && isSellableStoredSkin(selectedItem) && (
                                    <button onClick={() => handleAction('sell')} className="btn-sell">
                                        {selectedItem?.onSale ? 'Снять с продажи' : 'Продать'}
                                    </button>
                                )}
{!isFactionSkin && !isCurrentSkin && (
                                    <button onClick={() => handleAction('store')} className="btn-store">
                                        В шкаф
                                    </button>
)}
                            </>
                        )}
                    </div>
                </div>
            );
        }

        // 3) Выбран предмет/аксессуар
        const isEquipped = selectedItem.equipped;
        const isAccessory = selectedItem.id >= 1000 && selectedItem.id < 2000; // аксессуары (1000..1999)
        const showBackToCurrentSkin = isEquipped && isAccessory;
        //const showBackToCurrentSkin = isEquipped;

        const itemImg = getItemImage(selectedItem.icon);

        return (
            <div className="info-panel-content">
                <div className="info-header">
                    <div className="info-title">{selectedItem.name}</div>
                    <div className="info-big-icon">
                        {itemImg ? (
                            <img src={itemImg} alt="" />
                        ) : (
                            <div className="item-icon-placeholder">{selectedItem.name[0]}</div>
                        )}
                    </div>
                </div>

                <div className="info-scrollable-content">
                    <div className="info-desc">
                        {selectedItem.desc || ''} {/* Описание отсутствует */}
                        {isEquipped && <div className="status-green">Надето на вас</div>}
                    </div>
                </div>

                <div className="info-actions">
                    <button onClick={() => handleAction('use')} className="btn-use">
                        {getUseButtonText()}
                    </button>

                    {isEquipped && selectedItem.editable && (
                        <button onClick={() => handleAction('edit')} className="btn-edit">
                            Редактировать
                        </button>
                    )}

                  {!isEquipped && (isSellableStackItem(selectedItem) || isSellableAccessory(selectedItem)) && (
                        <button onClick={() => handleAction('sell')} className="btn-sell">
                                        {selectedItem?.onSale ? 'Снять с продажи' : 'Продать'}
                                    </button>
                )}

                    {!isEquipped && (
                        <button onClick={() => handleAction('drop')} className="btn-drop">
                            Выбросить
                        </button>
                    )}
                     {showBackToCurrentSkin && (
                        <button
                            onClick={() => (currentSkinItem ? setSelectedItem(currentSkinItem) : setSelectedItem(null))}
                            className="btn-back"
                        >
                            Назад
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
       <div className="inventory-wrapper">
    {/*         {invDbg && (
  <div className="inv-debug">
    payload: {invDbg.bytes} bytes / {invDbg.chars} chars
  </div> 
)}*/}
            <div className="inventory-container">

                                                            
                      <button
                        className="inventory-close"
                        onClick={closeInventory}
                        title="Закрыть (Esc)"
                        aria-label="Закрыть"
                        type="button"
                      >
                        ×
                      </button>
                      
                <div className="inventory-left">
                    <div className="character-header">Гардероб</div>
                    <div className="skins-container">{renderSkinSlots()}</div>
                </div>

                <div className="inventory-right">
                    <div className="inventory-header">
                        <div className="inventory-title">
                            <h2>Рюкзак</h2>
                            {totalPages > 1 && (
                                <div className="inventory-pager in-header">
                                    <button
                                        className={`pager-btn ${page <= 0 ? 'disabled' : ''}`}
                                        onClick={() => goToPage(page - 1)}
                                    >
                                        ◀
                                    </button>

                                    <div className="pager-info">
                                        {page + 1} / {totalPages}
                                    </div>

                                    <button
                                        className={`pager-btn ${page >= totalPages - 1 ? 'disabled' : ''}`}
                                        onClick={() => goToPage(page + 1)}
                                    >
                                        ▶
                                    </button>
                                </div>
                            )}
                        </div>
                       
                    <div className="header-right">
                      <div className="slots-info">
                        Занято: {occupiedTotal} / {data.maxSlots}
                      </div>
                    </div>
                    </div>

                    <div className="inventory-content">
                        <div className="slots-grid">{renderBackpackSlots()}</div>
                        <div className="item-info-panel">{renderInfoPanel()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
